/**
 * Generate Study Dialog - State Management
 *
 * Migrated from: lego/page/etmf/overview/generate-study/context/DialogState.jsx (416 lines)
 *
 * Complex state management for 7-step Generate Study Structure wizard including:
 * - Multi-step navigation
 * - Cascading dropdown data loading (Product → Study → Environment → Countries → Sites)
 * - Caching mechanism for API responses
 * - Form value management
 * - E-signature integration
 * - Final API submission
 */

import React, { useContext, useEffect, useRef, useState, ReactNode } from 'react'
import { DialogContext } from './DialogContext'
import { useServiceContext } from '../../../../../root'
import { useEsignature } from '../../../../../hooks/useEsignature'
import { MasterDataType } from '../../../../../constants/etmf'
import type { MasterDataResponse } from '../../../../../services/api/etmf'

interface SelectOption {
  value: number
  label: string
  id: number
  name: string
  mccObjUuid?: string
  mccParentObjUuid?: string
  realId?: number
  isCountry?: boolean
  isSite?: boolean
  siteIds?: string[]
  countryId?: string
}

interface Selection {
  product?: SelectOption
  study?: SelectOption
  environment?: SelectOption
  countries: SelectOption[]
  sites: SelectOption[]
}

interface FormValues {
  product: number | null
  study: number | null
  environment: number | null
  countries: number[]
  sites: string[]
}

interface DialogStateProps {
  closeModal: (showConfirmation?: boolean) => void
  children: ReactNode
}

export function DialogState({ closeModal, children }: DialogStateProps) {
  const context = useServiceContext()
  const { api, service, constant } = context
  const { translate } = service.filter
  const { signAction } = useEsignature()

  // Get linked entities (Clinical folder) on mount
  const clinicalFolderReq = useRef(
    api.etmf
      .getLinkedEntities()
      .then((data: any) => {
        return data.entityLinkMap.CLINICAL_ROOT
      })
      .catch(() => {
        console.error('Failed to load linked entities')
        return null
      })
  )

  // Dialog properties
  const [currentStep, setCurrentStep] = useState(1)
  const [title, setTitle] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [currentReq, setCurrentReq] = useState<Promise<any> | null>(null)

  // Options for dropdowns
  const [productOptions, setProductOptions] = useState<SelectOption[]>([])
  const [studyOptions, setStudyOptions] = useState<SelectOption[]>([])
  const [environmentOptions, setEnvironmentOptions] = useState<SelectOption[]>([])
  const [countryOptions, setCountryOptions] = useState<SelectOption[]>([])
  const [siteOptions, setSiteOptions] = useState<SelectOption[]>([])

  // Caching refs
  const environmentOptionsCached = useRef<Record<number, SelectOption[]>>({})
  const countryOptionsCached = useRef<Record<number, any[]>>({})
  const siteOptionsCached = useRef<Record<string, any>>({})

  const [selection, setSelection] = useState<Selection>({
    countries: [],
    sites: []
  })

  // Form values
  const defaultValues: FormValues = {
    product: null,
    study: null,
    environment: null,
    countries: [],
    sites: []
  }

  const values = useRef<FormValues>(defaultValues)

  // Handle loading state when currentReq changes
  useEffect(() => {
    if (currentReq) {
      setIsLoading(true)
      const req = currentReq

      currentReq
        .then(() => {
          if (req === currentReq) {
            setIsLoading(false)
          }
        })
        .catch(() => {
          if (req === currentReq) {
            setIsLoading(false)
          }
        })
    }
  }, [currentReq])

  // Update title and load data based on current step
  useEffect(() => {
    updateTitle()

    switch (currentStep) {
      case 2:
        loadProductOptions()
        return
      case 3:
        loadStudyOptions()
        return
      case 4:
        loadEnvOptions()
        return
      case 7:
        generateSelection()
        return
    }
  }, [currentStep])

  function updateTitle() {
    const baseTitle = translate('ETMF_GENERATE_TRIAL_STRCT_TITLE')
    let subtitle = ''

    switch (currentStep) {
      case 2:
        subtitle = translate('ETMF_GENERATE_TRIAL_STRCT_TITLE_STEP3')
        break
      case 3:
        subtitle = translate('ETMF_GENERATE_TRIAL_STRCT_TITLE_STEP4')
        break
      case 4:
        subtitle = translate('ETMF_GENERATE_TRIAL_STRCT_TITLE_STEP_ENVIRONMENTS')
        break
      case 5:
        subtitle = translate('ETMF_GENERATE_TRIAL_STRCT_TITLE_STEP5')
        break
      case 6:
        subtitle = translate('ETMF_GENERATE_TRIAL_STRCT_TITLE_STEP6')
        break
      case 7:
        subtitle = translate('ETMF_GENERATE_TRIAL_STRCT_TITLE_STEP7')
        break
    }

    setTitle(baseTitle + (subtitle ? ' - ' + subtitle : ''))
  }

  function transformToSelectOptions(data: any[], idField: string, nameField: string): SelectOption[] {
    return data.map((item) => ({
      value: item[idField],
      label: item[nameField],
      id: item[idField],
      name: item[nameField],
      ...item
    }))
  }

  function loadProductOptions() {
    const req = api.etmf
      .getMasterData(
        MasterDataType.PRODUCT,
        {
          sortBy: 'name',
          sortDesc: false
        },
        true,
        true
      )
      .then((response: any) => {
        // API returns array directly, not wrapped in response.data
        const data = Array.isArray(response) ? response : response.data || []
        const ops = transformToSelectOptions(data, 'id', 'name')
        setProductOptions(ops)
      })
      .catch((error: any) => {
        console.error('Failed to load products:', error)
      })
    setCurrentReq(req)
  }

  function loadStudyOptions() {
    if (studyOptions.length === 0) {
      const req = api.etmf
        .getMasterData(
          MasterDataType.TRIAL,
          {
            sortBy: 'name',
            sortDesc: false
          },
          true,
          true
        )
        .then((response: any) => {
          // API returns array directly, not wrapped in response.data
          const data = Array.isArray(response) ? response : response.data || []
          const ops = transformToSelectOptions(data, 'id', 'name')
          setStudyOptions(ops)
        })
        .catch((error: any) => {
          console.error('Failed to load studies:', error)
        })
      setCurrentReq(req)
    }
  }

  function loadEnvOptions() {
    const studyId = values.current.study
    if (!studyId) return

    const options = environmentOptionsCached.current[studyId]
    if (options) {
      setEnvironmentOptions(options)
    } else {
      setEnvironmentOptions([])
      const req = api.etmf
        .getMasterData(
          MasterDataType.ENVIRONMENT,
          {
            parentMdfId: studyId
          },
          true,
          true
        )
        .then((response: any) => {
          // API returns array directly, not wrapped in response.data
          const data = Array.isArray(response) ? response : response.data || []
          const ops = transformToSelectOptions(data, 'id', 'name')
          environmentOptionsCached.current[studyId] = ops
          setEnvironmentOptions(ops)
        })
        .catch((error: any) => {
          console.error('Failed to load environments:', error)
        })
      setCurrentReq(req)
    }
  }

  function loadCountryOptions(params: { sortDesc: boolean }): Promise<any> {
    return new Promise((resolve) => {
      const envId = values.current.environment
      if (!envId) {
        resolve({ elements: [], totalElements: 0, isCached: false })
        return
      }

      const options = countryOptionsCached.current[envId]

      if (options) {
        // Use cache
        postprocessCountryOptions(resolve, options, params, true)
      } else {
        const req = api.etmf
          .getMasterData(
            MasterDataType.COUNTRY,
            {
              parentMdfId: envId
            },
            false,
            true
          )
          .then((resp: any) => {
            const data = Array.isArray(resp) ? resp : resp.plain ? resp.plain() : []
            countryOptionsCached.current[envId] = data
            postprocessCountryOptions(resolve, data, params, false)
          })
          .catch((error: any) => {
            console.error('Failed to load countries:', error)
            resolve({ elements: [], totalElements: 0, isCached: false })
          })
        setCurrentReq(req)
      }
    })
  }

  function postprocessCountryOptions(resolve: any, countries: any[], params: { sortDesc: boolean }, isCached: boolean) {
    const selectedStudy = studyOptions.find((s) => s.id === values.current.study)

    if (selectedStudy && selectedStudy.mccObjUuid) {
      countries = countries.filter((country: any) => {
        return country.mccObjUuid && country.mccParentObjUuid === selectedStudy.mccObjUuid
      })
    }

    countries = sortArray(countries, params.sortDesc)
    setCountryOptions(transformToSelectOptions(countries, 'id', 'name'))

    resolve({
      elements: transformToSelectOptions(countries, 'id', 'name'),
      totalElements: countries.length,
      isCached
    })
  }

  function loadSiteOptions(params: { sortDesc: boolean }): Promise<any> {
    return new Promise((resolve) => {
      const countriesKey = JSON.stringify(values.current.countries)

      if (siteOptionsCached.current[countriesKey]) {
        const cachedData = siteOptionsCached.current[countriesKey]
        const countriesAndSites = postprocessSiteOptions(cachedData, params)
        resolve({
          elements: countriesAndSites,
          totalElements: countriesAndSites.length,
          isCached: true
        })
      } else {
        if (!values.current.countries || values.current.countries.length === 0) {
          resolve({
            elements: [],
            totalElements: 0,
            isCached: false
          })
        } else {
          const req = api.etmf
            .getSiteMasterDataPicklist(values.current.countries, {
              sortBy: 'name',
              sortDesc: false
            })
            .then((resp: any) => {
              const data = resp.plain ? resp.plain() : resp

              // Cache results
              siteOptionsCached.current[countriesKey] = data
              const countriesAndSites = postprocessSiteOptions(data, params)
              setSiteOptions(countriesAndSites)

              resolve({
                elements: countriesAndSites,
                totalElements: countriesAndSites.length,
                isCached: false
              })
            })
            .catch((error: any) => {
              console.error('Failed to load sites:', error)
              resolve({
                elements: [],
                totalElements: 0,
                isCached: false
              })
            })
          setCurrentReq(req)
        }
      }
    })
  }

  function postprocessSiteOptions(data: any, params: { sortDesc: boolean }): SelectOption[] {
    const countriesAndSites: SelectOption[] = []
    const dataClone = data.countryWithSitesList ? [...data.countryWithSitesList] : []

    if (params.sortDesc) {
      dataClone.reverse()
    }

    dataClone.forEach((countryAndSites: any) => {
      const country = countryAndSites.country
      const sites = countryAndSites.sites || []
      const siteIds = sites.map((site: any) => 'site' + site.id)

      const countryOb: SelectOption = {
        isCountry: true,
        id: country.id,
        value: country.id,
        label: country.name,
        name: country.name,
        realId: country.id,
        siteIds: siteIds
      }

      countriesAndSites.push(countryOb)

      sortArray(sites, params.sortDesc).forEach((site: any) => {
        countriesAndSites.push({
          isSite: true,
          id: site.id,
          value: site.id,
          label: site.name,
          name: site.name,
          realId: site.id,
          countryId: 'country' + country.id,
          siteIds: siteIds
        })
      })
    })

    return countriesAndSites
  }

  function sortArray(arr: any[], desc: boolean): any[] {
    const sorted = [...arr].sort((a, b) => a.name.localeCompare(b.name))
    if (desc) {
      sorted.reverse()
    }
    return sorted
  }

  function generateSelection() {
    const vals = values.current
    const settings: Selection = {
      product: productOptions.find((p) => p.id === vals.product),
      study: studyOptions.find((s) => s.id === vals.study),
      environment: environmentOptions.find((e) => e.id === vals.environment),
      countries: countryOptions.filter((country) => vals.countries.includes(country.id)),
      sites: siteOptions.filter((site) => site.isSite && vals.sites.includes('site' + site.id))
    }

    setSelection(settings)
  }

  async function submit(): Promise<void> {
    const request: any = {}

    request.siteMdfIds = selection.sites
      .map((s) => s.realId || s.id)
      .filter((id, index, self) => self.indexOf(id) === index)
    request.countryMdfIds = selection.countries.map((c) => c.id).filter((id, index, self) => self.indexOf(id) === index)
    request.parentId = null
    request.productMdfId = selection.product?.id
    request.studyMdfId = selection.study?.id
    request.envMdfId = selection.environment?.id

    // Handle e-signature if required
    let eSignatureValidation: any = null

    if (service.auth.requireESignatureForFileCommands()) {
      try {
        // Request e-signature authentication
        const validation = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_ETMF_GENERATE_TRIAL_STRUCTURE')

        // Extract only the fields needed by the backend API
        // For SSO/OKTA: backend expects only {relayState}
        // For DSO: backend expects only {username, password}
        if (validation.relayState) {
          eSignatureValidation = { relayState: validation.relayState }
        } else {
          eSignatureValidation = {
            username: validation.username || '',
            password: validation.password || ''
          }
        }
      } catch (error) {
        // User cancelled e-signature or authentication failed
        return
      }
    }

    // Execute the API call with validation
    try {
      const clinicalFolderId = await clinicalFolderReq.current

      if (!clinicalFolderId) {
        service.toast.error('Failed to get clinical folder ID')
        return
      }

      // Hard coded to 'Clinical' folder
      request.targetFsiId = clinicalFolderId

      await api.etmf.generateTrialStructure(request, eSignatureValidation)

      closeModal(false)
      service.toast.success('ETMF_GENERATE_TRIAL_STRCT_MSG_SUCCESS')
    } catch (error: any) {
      const errorMsg = error?.data?.message || error?.message || 'An error occurred'
      service.toast.error(errorMsg)
      throw error
    }
  }

  return (
    <DialogContext.Provider
      value={{
        title,
        currentStep,
        isLoading,
        selection,

        productOptions,
        studyOptions,
        siteOptions,
        environmentOptions,
        countryOptions,

        getCurrentValues: () => {
          return values.current
        },
        getDefaultValues: () => {
          return { ...defaultValues }
        },
        getStepName: (num: number) => {
          switch (num) {
            case 2:
              return 'product'
            case 3:
              return 'study'
            case 4:
              return 'environment'
            case 5:
              return 'countries'
            case 6:
              return 'sites'
            default:
              return null
          }
        },
        goToNextStep: (vals: FormValues) => {
          values.current = vals
          setCurrentStep(currentStep + 1)
        },
        goToPreviousStep: () => {
          setCurrentStep(currentStep - 1)
        },
        loadCountryOptions,
        loadSiteOptions,
        submit
      }}
    >
      {children}
    </DialogContext.Provider>
  )
}
